import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Phone, MessageSquare, Copy, X } from "lucide-react";
import { useT } from "@/lib/i18n";
import { PHONE_E164 } from "@/lib/data";
import { toast } from "sonner";

export const BookingModal = ({ open, onOpenChange, lang, cabinName, message }) => {
  const t = useT(lang);

  const smsHref = `sms:${PHONE_E164}?body=${encodeURIComponent(message)}`;
  const telHref = `tel:${PHONE_E164}`;

  const copyMsg = async () => {
    try {
      await navigator.clipboard.writeText(message);
      toast.success(t.booking.copied);
    } catch {
      toast.error("—");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="booking-modal"
        className="bg-[#f7f1e6] border-[#a17a4c]/30 max-w-md"
      >
        <DialogHeader>
          <DialogTitle
            data-testid="booking-modal-title"
            className="font-serif-display text-2xl text-[#4a3520]"
          >
            {t.booking.title}
          </DialogTitle>
          <DialogDescription className="text-[#5a4530]">
            {t.booking.desc}
          </DialogDescription>
        </DialogHeader>

        <div className="my-3">
          <div className="text-[11px] uppercase tracking-wider text-[#8a6a44] mb-2">
            {t.booking.messagePreview} · {cabinName}
          </div>
          <div
            data-testid="booking-message-preview"
            className="rounded-lg border border-[#a17a4c]/30 bg-[#fbf6ea] p-4 text-sm text-[#4a3520] leading-relaxed whitespace-pre-line"
          >
            {message}
          </div>
          <button
            data-testid="booking-copy-btn"
            onClick={copyMsg}
            className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#8a6a3f] hover:text-[#4a3520] transition"
          >
            <Copy className="w-3.5 h-3.5" />
            {t.booking.copy}
          </button>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-3">
          <Button
            data-testid="booking-cancel-btn"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="sm:w-auto w-full border-[#a17a4c]/40 text-[#4a3520] hover:bg-[#efe5d3]"
          >
            <X className="w-4 h-4 mr-2" />
            {t.booking.cancel}
          </Button>
          <Button
            asChild
            data-testid="booking-call-btn"
            variant="outline"
            className="sm:w-auto w-full border-[#8a6a3f] text-[#4a3520] hover:bg-[#efe5d3]"
          >
            <a href={telHref}>
              <Phone className="w-4 h-4 mr-2" />
              {t.booking.call}
            </a>
          </Button>
          <Button
            asChild
            data-testid="booking-sms-btn"
            className="sm:w-auto w-full bg-[#8a6a3f] hover:bg-[#7a5d38] text-[#f7f1e6]"
          >
            <a href={smsHref}>
              <MessageSquare className="w-4 h-4 mr-2" />
              {t.booking.sms}
            </a>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
